import { useEffect } from 'react'

import { fetchPropertyRates } from 'actions/PropertyActions'
import { OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_BED_BANK } from 'constants/offer'
import { OptimizelyFeatureFlags } from 'constants/featureFlags'
import usePropertyOfferMapping from 'hooks/Offers/usePropertyOfferMapping'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import buildPropertyRatesKey from 'lib/offer/buildPropertyRatesKey'
import { isBedbankOffer, isLpcHotelOffer } from 'lib/offer/offerTypes'
import useUserHasLoyaltyBenefit from 'loyalty/hooks/useUserHasLoyaltyBenefit'
import getIsCurrentRegionUserLoyaltyRegion from 'loyalty/selectors/getIsCurrentRegionUserLoyaltyRegion'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import getSearchTileHasLoyaltyPackageUpgrade from 'loyalty/selectors/getSearchTileHasLoyaltyPackageUpgrade'

interface Params {
  checkIn: string | undefined
  checkOut: string | undefined
  rooms: Array<App.Occupants> | undefined
  /** When `false`, the banner can't render so no network calls are made. */
  enabled?: boolean
}

export interface SearchPropertyRateLoyaltyPackageUpgradeStatus {
  available: boolean
  /** Eligible but still resolving — gate the tile's reveal on this to avoid a late banner pop-in. */
  fetching: boolean
}

/**
 * Whether the loyalty package-upgrade banner should show on a dated SPP/LPC or bedbank/LPP search tile,
 * and whether that's still resolving. Ineligible tiles make no network calls.
 *
 * Bedbank mirrors `BedbankOfferPage`'s `shouldUseSpp`: show only for a valid bedbank-property mapping —
 * otherwise the banner would be a dead click-through to a page with no upgrade UI. Promotional offers
 * used to need the `spp_bedbank_promos` experiment; that concluded in HP-2732, so they always take the
 * SPP path now.
 */
function useSearchPropertyRateLoyaltyPackageUpgrade(
  offer: App.AnyOffer | undefined,
  { checkIn, checkOut, rooms, enabled = true }: Params,
): SearchPropertyRateLoyaltyPackageUpgradeStatus {
  const dispatch = useAppDispatch()

  const isLoyaltyEnabled = useAppSelector(getIsLoyaltyEnabled)
  const isCurrentRegionUserLoyaltyRegion = useAppSelector(getIsCurrentRegionUserLoyaltyRegion)
  const hasUpgradeBenefit = useUserHasLoyaltyBenefit('hotel_upgrade')
  const isTargetPackageUpgradesEnabled = useAppSelector(
    (state) => !!state.optimizely.optimizelyFeatureFlags[OptimizelyFeatureFlags.loyaltySppPackageUpgrades],
  )

  const isBedbank = isBedbankOffer(offer)
  const isSupportedOffer = isLpcHotelOffer(offer) || isBedbank
  const isDated = !!(checkIn && checkOut && rooms?.length)
  // An upgrade can only be applied to a single-room booking, so there is nothing to advertise once a
  // second room is added — the offer page hides its own chip in `getHasUpgradeableRate`, and clicking
  // through to it would find no upgrade to apply. Gating here also skips the fetches below.
  const isSingleRoom = rooms?.length === 1

  // Gates the mapping fetch; the async bedbank mapping-validity check is applied afterwards.
  const isCandidate =
    enabled &&
    isLoyaltyEnabled &&
    isCurrentRegionUserLoyaltyRegion &&
    hasUpgradeBenefit &&
    isTargetPackageUpgradesEnabled &&
    isDated &&
    isSingleRoom &&
    isSupportedOffer

  const offerType = isBedbank ? OFFER_TYPE_BED_BANK : OFFER_TYPE_ALWAYS_ON
  const [mapping, mappingFetching] = usePropertyOfferMapping(offerType, offer?.id ?? '', { disabled: !isCandidate })
  const propertyId = mapping?.propertyId

  const hasValidMapping = isBedbank ? !!mapping?.bedbankPropertyIds?.includes(offer?.id ?? '') : !!propertyId
  const isEligible = isCandidate && hasValidMapping && !!propertyId

  const ratesKey =
    isEligible && propertyId && checkIn && checkOut
      ? buildPropertyRatesKey({
          propertyId,
          checkIn,
          checkOut,
          occupancies: rooms ?? [],
          utmContent: undefined,
          roomTypeId: undefined,
          roomRateId: undefined,
        })
      : undefined

  useEffect(() => {
    if (isEligible && propertyId && checkIn && checkOut && rooms) {
      // Cached with no TTL → at most one call per dated, eligible tile.
      dispatch(
        fetchPropertyRates(propertyId, {
          checkIn,
          checkOut,
          occupancies: rooms,
          utmContent: undefined,
          roomTypeId: undefined,
          roomRateId: undefined,
        }),
      )
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, ratesKey])

  const ratesPending = useAppSelector((state) => {
    if (!ratesKey) return false
    const record = state.offer.propertyRates[ratesKey]
    return !record || !!record.fetching
  })

  const available = useAppSelector(
    (state) =>
      isEligible &&
      getSearchTileHasLoyaltyPackageUpgrade(state, {
        propertyId,
        checkIn,
        checkOut,
        occupancies: rooms ?? [],
      }),
  )

  // Hold the tile's reveal while a candidate is still resolving (mapping fetching, or rates pending).
  const fetching = isCandidate && (mappingFetching || (isEligible && ratesPending))

  return { available, fetching }
}

export default useSearchPropertyRateLoyaltyPackageUpgrade
